import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Star, ChevronLeft, MessageSquare, ThumbsUp, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { reviewsApi } from '@/services/clinicApi';
import { toast } from 'sonner';

const EMPTY_DISTRIBUTION = [
  { stars: 5, count: 0, percentage: 0 },
  { stars: 4, count: 0, percentage: 0 },
  { stars: 3, count: 0, percentage: 0 },
  { stars: 2, count: 0, percentage: 0 },
  { stars: 1, count: 0, percentage: 0 },
];

const EMPTY_SUMMARY = {
  average: 0,
  total: 0,
  distribution: EMPTY_DISTRIBUTION,
};

const resolveDoctorIdFromUser = (currentUser) => {
  const partnerId = currentUser?.partner_id;
  if (typeof partnerId === 'number') return partnerId;
  if (typeof partnerId === 'string' && partnerId.trim() && !Number.isNaN(Number(partnerId))) {
    return Number(partnerId);
  }
  if (partnerId && typeof partnerId === 'object' && partnerId.id) {
    return Number(partnerId.id);
  }
  if (currentUser?.doctor?.id) {
    return Number(currentUser.doctor.id);
  }
  if (currentUser?.partner?.id) {
    return Number(currentUser.partner.id);
  }
  if (currentUser?.id && !Number.isNaN(Number(currentUser.id))) {
    return Number(currentUser.id);
  }
  return null;
};

const normalizeReviewDate = (value) => {
  const date = value ? new Date(value) : null;
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
};

const buildPatientName = (review, lang) => {
  const firstName = review.patient?.first_name || '';
  const lastName = review.patient?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    review.patient_name ||
    review.patient?.name ||
    fullName ||
    review.user?.name ||
    (lang === 'ar' ? 'مريض' : 'Patient')
  );
};

const buildSummary = (reviewsData, apiAverage = null) => {
  if (!reviewsData.length) {
    return EMPTY_SUMMARY;
  }

  const total = reviewsData.length;
  const sum = reviewsData.reduce((acc, review) => acc + review.rating, 0);
  const computedAverage = sum / total;
  const average = Number.isFinite(apiAverage) && apiAverage > 0 ? apiAverage : computedAverage;
  const distribution = [5, 4, 3, 2, 1].map((stars) => {
    const count = reviewsData.filter((review) => Math.round(review.rating) === stars).length;
    return {
      stars,
      count,
      percentage: Math.round((count / total) * 100),
    };
  });

  return {
    average: Number(average.toFixed(1)),
    total,
    distribution,
  };
};

const DoctorReviews = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchReviews();
  }, [isAuthenticated, navigate]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const doctorId = resolveDoctorIdFromUser(user);
      if (!doctorId) {
        throw new Error('Doctor ID is missing');
      }

      const response = await reviewsApi.getDoctorReviews(doctorId);
      const payload = response.data?.data || response.data || {};
      const reviewsData = Array.isArray(payload?.reviews)
        ? payload.reviews
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

      const transformedReviews = reviewsData.map((review) => ({
        id: review.id,
        patient: {
          name: buildPatientName(review, lang),
          image:
            review.patient?.media?.[0]?.url ||
            review.user?.media?.[0]?.url ||
            review.patient?.avatar ||
            review.user?.avatar ||
            null,
        },
        rating: Number(review.rate || review.rating || 0),
        comment: review.review || '',
        date: normalizeReviewDate(review.created_at || review.date),
        helpful: Number(review.helpful_count || 0),
      }));

      setReviews(transformedReviews);
      setSummary(
        buildSummary(
          transformedReviews,
          Number(payload?.average_rating ?? payload?.rating ?? 0)
        )
      );
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      setReviews([]);
      setSummary(EMPTY_SUMMARY);
      toast.error(lang === 'ar' ? 'فشل في تحميل التقييمات' : 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-reviews">
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'التقييمات والمراجعات' : 'Reviews & Ratings'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'عرض تقييمات المرضى' : 'View patient feedback'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="icon" onClick={fetchReviews} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <Card className="dark:bg-slate-800 border-0 shadow-sm">
          <CardContent className="p-6">
            {loading ? (
              <div className="flex gap-8">
                <Skeleton className="h-32 w-32" />
                <div className="flex-1 space-y-3">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <Skeleton key={item} className="h-4 w-full" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-slate-900 dark:text-white mb-2">
                    {summary.average || '0.0'}
                  </div>
                  <div className="flex justify-center gap-1 mb-2">
                    {[...Array(5)].map((_, index) => (
                      <Star
                        key={index}
                        className={`h-5 w-5 ${
                          index < Math.round(summary.average)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-slate-300 dark:text-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    {summary.total} {lang === 'ar' ? 'تقييم' : 'reviews'}
                  </p>
                </div>

                <div className="flex-1 space-y-2">
                  {summary.distribution.map((item) => (
                    <div key={item.stars} className="flex items-center gap-3">
                      <span className="text-sm text-slate-600 dark:text-slate-400 w-16">
                        {item.stars} {lang === 'ar' ? 'نجوم' : 'stars'}
                      </span>
                      <Progress value={item.percentage} className="flex-1 h-2" />
                      <span className="text-sm text-slate-500 dark:text-slate-400 w-12 text-right">
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="dark:bg-slate-800 border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {lang === 'ar' ? 'المراجعات' : 'Reviews'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-6">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="space-y-3">
                    <div className="flex gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/4" />
                        <Skeleton className="h-3 w-1/6" />
                      </div>
                    </div>
                    <Skeleton className="h-16 w-full" />
                  </div>
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <Star className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <p className="text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'لا توجد تقييمات بعد' : 'No reviews yet'}
                </p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                  {lang === 'ar' ? 'ستظهر تقييمات المرضى هنا' : 'Patient reviews will appear here'}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review, index) => (
                  <div key={review.id} data-testid={`review-${review.id}`}>
                    <div className="flex gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={review.patient.image} />
                        <AvatarFallback className="bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300">
                          {review.patient.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-slate-900 dark:text-white">{review.patient.name}</h4>
                          <span className="text-sm text-slate-400 dark:text-slate-500">
                            {format(review.date, 'PP', { locale })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(5)].map((_, starIndex) => (
                            <Star
                              key={starIndex}
                              className={`h-4 w-4 ${
                                starIndex < review.rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-slate-300 dark:text-slate-600'
                              }`}
                            />
                          ))}
                        </div>
                        {review.comment && (
                          <p className="text-slate-600 dark:text-slate-300 mt-3">{review.comment}</p>
                        )}
                        {review.helpful > 0 && (
                          <Button variant="ghost" size="sm" className="mt-2 text-slate-500 dark:text-slate-400">
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            {lang === 'ar' ? 'مفيد' : 'Helpful'} ({review.helpful})
                          </Button>
                        )}
                      </div>
                    </div>
                    {index < reviews.length - 1 && <div className="border-b dark:border-slate-700 mt-6" />}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorReviews;
